import {
  AccessTime as AccessTimeIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { Box, Card, CardContent, Chip, Grid, Skeleton, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { GridColDef } from "@mui/x-data-grid";
import { Link } from "react-router";
import { DataGrid } from "../../../components/data-grid";
import { theme } from "../../../theme/theme";
import type { CoverageMetrics, TestCoverageInfo } from "../../../types";

interface CoverageTabProps {
  data?: CoverageMetrics;
  isLoading?: boolean;
}

function CoverageGauge({ percentage, isLoading }: { percentage: number; isLoading?: boolean }) {
  const getColor = (pct: number) => {
    if (pct >= 80) return theme.palette.success.main;
    if (pct >= 50) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  if (isLoading) {
    return <Skeleton variant="circular" width={160} height={160} />;
  }

  const color = getColor(percentage);

  return (
    <Box
      sx={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          width: 160,
          height: 160,
          borderRadius: "50%",
          background: `conic-gradient(${color} ${percentage * 3.6}deg, ${alpha(color, 0.15)} 0deg)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: 130,
            height: 130,
            borderRadius: "50%",
            backgroundColor: "background.paper",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <Typography variant="h3" sx={{ fontWeight: 700, color }}>
            {percentage.toFixed(0)}%
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Coverage
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

const neverRunColumns: GridColDef<TestCoverageInfo>[] = [
  {
    field: "test_name",
    headerName: "Test Name",
    flex: 2,
    minWidth: 200,
    renderCell: (params) => (
      <Link
        to={`/tests/${params.row.test_id}`}
        style={{ color: theme.palette.primary.main, textDecoration: "none" }}
      >
        {params.value}
      </Link>
    ),
  },
  {
    field: "created_at",
    headerName: "Created",
    width: 150,
    valueFormatter: (value) => (value ? new Date(value).toLocaleDateString() : "-"),
  },
];

const notRunRecentlyColumns: GridColDef<TestCoverageInfo>[] = [
  {
    field: "test_name",
    headerName: "Test Name",
    flex: 2,
    minWidth: 200,
    renderCell: (params) => (
      <Link
        to={`/tests/${params.row.test_id}`}
        style={{ color: theme.palette.primary.main, textDecoration: "none" }}
      >
        {params.value}
      </Link>
    ),
  },
  {
    field: "days_since_last_run",
    headerName: "Days Inactive",
    width: 130,
    align: "center",
    headerAlign: "center",
    renderCell: (params) => {
      const days = params.value as number;
      const color =
        days > 30
          ? "error"
          : days > 14
            ? "warning"
            : ("default" as "error" | "warning" | "default");
      return <Chip label={`${days} days`} size="small" color={color} />;
    },
  },
  {
    field: "last_run_at",
    headerName: "Last Run",
    width: 150,
    valueFormatter: (value) => (value ? new Date(value).toLocaleDateString() : "Never"),
  },
];

export function CoverageTab({ data, isLoading }: CoverageTabProps) {
  const testsNeverRun = data?.tests_never_run ?? [];
  const testsNotRunRecently = data?.tests_not_run_recently ?? [];
  const totalTests = data?.total_tests ?? 0;
  const testsWithRuns = data?.tests_with_runs ?? 0;
  const coveragePercentage = data?.coverage_percentage ?? 0;

  return (
    <Stack spacing={3}>
      {/* Overview Cards */}
      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: 240 }}>
            <CardContent
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Stack alignItems="center" spacing={1}>
                <CoverageGauge percentage={coveragePercentage} isLoading={isLoading} />
                <Typography variant="body2" color="text.secondary">
                  {testsWithRuns} of {totalTests} tests executed
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, md: 4 }}>
          <Card
            sx={{
              height: 240,
              background: alpha(theme.palette.error.main, 0.03),
            }}
          >
            <CardContent>
              <Stack spacing={2} sx={{ height: "100%" }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <BlockIcon color="error" fontSize="small" />
                  <Typography variant="subtitle1">Never Executed</Typography>
                </Stack>
                {isLoading ? (
                  <Skeleton variant="rectangular" height={60} />
                ) : (
                  <Typography variant="h2" color="error.main" sx={{ fontWeight: 700 }}>
                    {testsNeverRun.length}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  Tests that have never been run
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, md: 4 }}>
          <Card
            sx={{
              height: 240,
              background: alpha(theme.palette.warning.main, 0.03),
            }}
          >
            <CardContent>
              <Stack spacing={2} sx={{ height: "100%" }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <AccessTimeIcon color="warning" fontSize="small" />
                  <Typography variant="subtitle1">Stale Tests</Typography>
                </Stack>
                {isLoading ? (
                  <Skeleton variant="rectangular" height={60} />
                ) : (
                  <Typography variant="h2" color="warning.main" sx={{ fontWeight: 700 }}>
                    {testsNotRunRecently.length}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  Tests not run in the selected period
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tables */}
      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <BlockIcon color="error" fontSize="small" />
                <Typography variant="h6">Tests Never Executed</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                These tests exist but have never been run
              </Typography>
              <Box sx={{ height: 400 }}>
                {testsNeverRun.length > 0 ? (
                  <DataGrid
                    rows={testsNeverRun.map((t, idx) => ({
                      ...t,
                      id: t.test_id || idx,
                    }))}
                    columns={neverRunColumns}
                    loading={isLoading}
                    pageSizeOptions={[10, 25]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 10 } },
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 48, color: "success.main", mb: 2 }} />
                    <Typography color="text.secondary">
                      All tests have been executed at least once
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <AccessTimeIcon color="warning" fontSize="small" />
                <Typography variant="h6">Stale Tests</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                Tests not run during the selected period
              </Typography>
              <Box sx={{ height: 400 }}>
                {testsNotRunRecently.length > 0 ? (
                  <DataGrid
                    rows={testsNotRunRecently.map((t, idx) => ({
                      ...t,
                      id: t.test_id || idx,
                    }))}
                    columns={notRunRecentlyColumns}
                    loading={isLoading}
                    pageSizeOptions={[10, 25]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 10 } },
                      sorting: {
                        sortModel: [{ field: "days_since_last_run", sort: "desc" }],
                      },
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 48, color: "success.main", mb: 2 }} />
                    <Typography color="text.secondary" textAlign="center">
                      All tests have been run in the
                      <br />
                      selected period at least once
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
}
